import { lazy, Suspense } from "react";
import { createBrowserRouter, RouterProvider } from "react-router";

import { ProtectedRoute } from "@/auth/ProtectedRoute";

/**
 * Rotas com code splitting: a landing page pública não carrega o bundle da
 * aplicação, e cada área do sistema só chega quando é visitada.
 */
const LandingPage = lazy(() => import("@/routes/LandingPage"));
const LoginPage = lazy(() => import("@/routes/LoginPage"));
const RegisterPage = lazy(() => import("@/routes/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("@/routes/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/routes/ResetPasswordPage"));
const NotFoundPage = lazy(() => import("@/routes/NotFoundPage"));

// Descoberta pública: funcionam sem sessão e ficam FORA do `ProtectedRoute`.
const PublicSearchPage = lazy(() => import("@/routes/PublicSearchPage"));
const PublicOpportunityPage = lazy(
  () => import("@/routes/PublicOpportunityPage"),
);

const AppLayout = lazy(() => import("@/routes/app/AppLayout"));
const DashboardPage = lazy(() => import("@/routes/app/DashboardPage"));
const SearchPage = lazy(() => import("@/routes/app/SearchPage"));
const UnlockedPage = lazy(() => import("@/routes/app/UnlockedPage"));
const CreditsPage = lazy(() => import("@/routes/app/CreditsPage"));
const SubscriptionPage = lazy(() => import("@/routes/app/SubscriptionPage"));
const BillingReturnPage = lazy(() => import("@/routes/app/BillingReturnPage"));
const SpecializedSearchesPage = lazy(
  () => import("@/routes/app/SpecializedSearchesPage"),
);
const AccountPage = lazy(() => import("@/routes/app/AccountPage"));

function RouteFallback() {
  return <output aria-live="polite">Carregando…</output>;
}

const router = createBrowserRouter([
  { path: "/", element: <LandingPage /> },
  { path: "/login", element: <LoginPage /> },
  { path: "/register", element: <RegisterPage /> },
  { path: "/forgot-password", element: <ForgotPasswordPage /> },
  { path: "/reset-password", element: <ResetPasswordPage /> },
  { path: "/oportunidades", element: <PublicSearchPage /> },
  { path: "/oportunidades/:id", element: <PublicOpportunityPage /> },
  {
    element: <ProtectedRoute />,
    children: [
      {
        path: "/app",
        element: <AppLayout />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: "search", element: <SearchPage /> },
          { path: "unlocked", element: <UnlockedPage /> },
          { path: "credits", element: <CreditsPage /> },
          { path: "subscription", element: <SubscriptionPage /> },
          // Retorno do checkout do gateway. Só UX: não confirma pagamento.
          { path: "billing/return", element: <BillingReturnPage /> },
          { path: "specialized-searches", element: <SpecializedSearchesPage /> },
          { path: "account", element: <AccountPage /> },
        ],
      },
    ],
  },
  { path: "*", element: <NotFoundPage /> },
]);

export function AppRouter() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <RouterProvider router={router} />
    </Suspense>
  );
}
